/**
 * LeaWidget — the floating Lea panel (every signed-in page).
 *
 * Header: conversations menu · "Lea Chat" · new chat · open the full Lea page · close.
 * The conversation itself is LeaChatView, shared with the Lea Assistant page. Hidden on that page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, MessageSquarePlus, PanelRight, Trash2, X } from 'lucide-react';
import { useT, tr } from '@/i18n';
import { LeaApi, type LeaConversation } from '@/services/lea.service';
import { LeaChatView, LeaOrb } from './LeaChatView';
import styles from './LeaWidget.module.css';

const CID_KEY = 'lea:widget:cid';

export const LeaWidget: React.FC = () => {
  const { locale } = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() => sessionStorage.getItem(CID_KEY));
  const [conversations, setConversations] = useState<LeaConversation[]>([]);
  const [resetKey, setResetKey] = useState(0);

  const refreshList = useCallback(() => {
    LeaApi.conversations(locale).then((r) => setConversations(r.items)).catch(() => {});
  }, [locale]);

  useEffect(() => {
    if (conversationId) sessionStorage.setItem(CID_KEY, conversationId); else sessionStorage.removeItem(CID_KEY);
  }, [conversationId]);
  useEffect(() => { if (open) refreshList(); }, [open, refreshList]);

  if (pathname.startsWith('/user/lea-assistant')) return null;

  const newChat = () => { setConversationId(null); setResetKey((k) => k + 1); setListOpen(false); };
  const pick = (id: string) => { setConversationId(id); setListOpen(false); };
  const remove = async (id: string) => {
    try { await LeaApi.deleteConversation(locale, id); } catch { /* already gone */ }
    if (id === conversationId) newChat();
    refreshList();
  };
  const current = conversations.find((c) => c.id === conversationId);

  const panel = (
    <div className={styles.panel} role="dialog" aria-label={tr('lea.title', 'Lea assistant')} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <header className={styles.head}>
        <button type="button" className={`${styles.round} ${listOpen ? styles.roundOn : ''}`} onClick={() => { setListOpen((v) => !v); refreshList(); }} aria-label={tr('lea.conversations', 'Conversations')} aria-expanded={listOpen}>
          <Menu size={18} />
        </button>
        <button type="button" className={styles.titleBtn} onClick={() => setListOpen((v) => !v)}>
          <span className={styles.titleText}>{current?.title || tr('lea.chat_title', 'Lea Chat')}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        <span className={styles.spacer} />
        <button type="button" className={styles.round} onClick={newChat} aria-label={tr('lea.new_chat', 'New chat')} title={tr('lea.new_chat', 'New chat')}>
          <MessageSquarePlus size={18} />
        </button>
        <button type="button" className={styles.round} onClick={() => { setOpen(false); navigate(`/user/lea-assistant/chat${conversationId ? `?c=${conversationId}` : ''}`); }} aria-label={tr('lea.open_page', 'Open Lea page')} title={tr('lea.open_page', 'Open Lea page')}>
          <PanelRight size={18} />
        </button>
        <button type="button" className={styles.round} onClick={() => setOpen(false)} aria-label={tr('common.close', 'Close')}>
          <X size={18} />
        </button>
      </header>

      {listOpen && (
        <div className={styles.list} role="menu">
          <button type="button" className={styles.listNew} onClick={newChat} role="menuitem">
            <MessageSquarePlus size={16} aria-hidden="true" />{tr('lea.new_chat', 'New chat')}
          </button>
          {conversations.length === 0 && <p className={styles.listEmpty}>{tr('lea.no_conversations', 'No conversations yet.')}</p>}
          {conversations.map((c) => (
            <div key={c.id} className={`${styles.listRow} ${c.id === conversationId ? styles.listRowOn : ''}`}>
              <button type="button" className={styles.listItem} onClick={() => pick(c.id)} role="menuitem">
                {c.title || tr('lea.untitled', 'Untitled chat')}
              </button>
              <button type="button" className={styles.listDelete} onClick={() => remove(c.id)} aria-label={tr('common.delete', 'Delete')}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <LeaChatView
        conversationId={conversationId}
        onConversationId={(id) => { setConversationId(id); refreshList(); }}
        resetKey={resetKey}
        onReply={refreshList}
        onNavigate={() => { if (window.innerWidth < 700) setOpen(false); }}
        compact
      />
    </div>
  );

  return (
    <>
      {!open && (
        <button
          type="button"
          className={`${styles.launcher} lea-launcher ${/^\/user\/course\/\d+\/activity\//.test(pathname) ? styles.launcherRaised : ''}`}
          onClick={() => setOpen(true)}
          aria-label={tr('lea.open', 'Open Lea assistant')}
        >
          <LeaOrb size={40} />
          <span className={styles.launcherText}>{tr('lea.launcher', 'Ask Lea')}</span>
        </button>
      )}
      {open && createPortal(panel, document.body)}
    </>
  );
};

export default LeaWidget;
